import { useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Video, Music, Pencil, Cloud, CheckCircle2, Upload, Camera, Play, Trash2, ArrowLeft, Plus, X } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Landing Page Component
const LandingPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    role: "",
    content_type: "",
    wants_updates: false,
  });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    if (!formData.full_name || !formData.email || !formData.role) {
      setError("Por favor completa todos los campos requeridos");
      setLoading(false);
      return;
    }

    try {
      await axios.post(`${API}/beta-register`, formData);
      setSubmitted(true);
    } catch (err) {
      if (err.response?.data?.detail) {
        setError(err.response.data.detail);
      } else {
        setError("Error al registrarse. Por favor intenta de nuevo.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-6 md:p-8 max-w-md w-full text-center" data-testid="success-message">
          <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
            <CheckCircle2 className="w-10 h-10 text-green-600" />
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
            ¡Bienvenido a ArtiBook!
          </h2>
          <p className="text-gray-600 mb-6 text-sm md:text-base">
            Te has registrado exitosamente en la beta. ¡Ahora puedes empezar a usar la aplicación!
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            className="bg-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-purple-700 transition mb-3 w-full text-sm md:text-base"
            data-testid="go-to-app-button"
          >
            Ir a la Aplicación
          </button>
          <button
            onClick={() => {
              setSubmitted(false);
              setFormData({
                full_name: "",
                email: "",
                role: "",
                content_type: "",
                wants_updates: false,
              });
            }}
            className="text-purple-600 hover:text-purple-700 transition text-sm"
          >
            Registrar otro usuario
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900">
      <div className="container mx-auto px-4 py-8 md:py-16">
        {/* Header with App Button */}
        <div className="flex justify-end mb-6 md:mb-8">
          <button
            onClick={() => navigate('/dashboard')}
            className="bg-white text-purple-600 px-4 md:px-6 py-2 rounded-lg font-semibold hover:bg-purple-50 transition text-sm md:text-base"
            data-testid="enter-app-button"
          >
            Entrar a la App
          </button>
        </div>

        {/* Hero Section */}
        <div className="text-center mb-12 md:mb-16" data-testid="hero-section">
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-4 md:mb-6">
            Arti<span className="text-yellow-400">Book</span>
          </h1>
          <p className="text-xl md:text-3xl text-purple-200 mb-2 md:mb-4 font-light">
            Graba. Anota. Inspira.
          </p>
          <p className="text-lg md:text-xl text-purple-100 mb-4 md:mb-8">
            Record. Annotate. Inspire.
          </p>
          <p className="text-base md:text-lg text-purple-200 max-w-2xl mx-auto px-4">
            El cuaderno digital donde los artistas guardan su movimiento y su música.
            Donde la danza y la música se encuentran.
          </p>
        </div>

        {/* Features Section */}
        <div className="grid md:grid-cols-3 gap-6 md:gap-8 mb-12 md:mb-16" data-testid="features-section">
          <div className="bg-white/10 backdrop-blur-lg rounded-xl p-5 md:p-6 text-white hover:bg-white/20 transition">
            <Video className="w-10 h-10 md:w-12 md:h-12 mb-3 md:mb-4 text-yellow-400" />
            <h3 className="text-lg md:text-xl font-bold mb-2">Graba tus Coreografías</h3>
            <p className="text-purple-100 text-sm md:text-base">
              Captura cada movimiento y composición musical directamente desde tu dispositivo.
            </p>
          </div>

          <div className="bg-white/10 backdrop-blur-lg rounded-xl p-5 md:p-6 text-white hover:bg-white/20 transition">
            <Pencil className="w-10 h-10 md:w-12 md:h-12 mb-3 md:mb-4 text-yellow-400" />
            <h3 className="text-lg md:text-xl font-bold mb-2">Añade Anotaciones</h3>
            <p className="text-purple-100 text-sm md:text-base">
              Añade notas y comentarios a tus grabaciones para recordar cada detalle importante.
            </p>
          </div>

          <div className="bg-white/10 backdrop-blur-lg rounded-xl p-5 md:p-6 text-white hover:bg-white/20 transition">
            <Cloud className="w-10 h-10 md:w-12 md:h-12 mb-3 md:mb-4 text-yellow-400" />
            <h3 className="text-lg md:text-xl font-bold mb-2">Almacenamiento en la Nube</h3>
            <p className="text-purple-100 text-sm md:text-base">
              Accede a tu contenido desde cualquier dispositivo, sin llenar tu móvil.
            </p>
          </div>
        </div>

        {/* Who is it for Section */}
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 md:p-8 mb-12 md:mb-16 text-white" data-testid="target-audience-section">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 text-center">¿Para Quién es ArtiBook?</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-14 h-14 md:w-16 md:h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4">
                <Music className="w-7 h-7 md:w-8 md:h-8" />
              </div>
              <h3 className="font-bold text-lg md:text-xl mb-2">Bailarines</h3>
              <p className="text-purple-100 text-sm md:text-base">Guarda y perfecciona tus coreografías</p>
            </div>

            <div className="text-center">
              <div className="w-14 h-14 md:w-16 md:h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4">
                <Music className="w-7 h-7 md:w-8 md:h-8" />
              </div>
              <h3 className="font-bold text-lg md:text-xl mb-2">Músicos</h3>
              <p className="text-purple-100 text-sm md:text-base">Organiza tus composiciones musicales</p>
            </div>

            <div className="text-center">
              <div className="w-14 h-14 md:w-16 md:h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4">
                <Music className="w-7 h-7 md:w-8 md:h-8" />
              </div>
              <h3 className="font-bold text-lg md:text-xl mb-2">Profesores</h3>
              <p className="text-purple-100 text-sm md:text-base">Comparte y gestiona contenido con tus estudiantes</p>
            </div>
          </div>
        </div>

        {/* Beta Registration Form */}
        <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-2xl p-6 md:p-8" data-testid="beta-form">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2 text-center">
            Únete a la Beta
          </h2>
          <p className="text-gray-600 mb-6 text-center text-sm md:text-base">
            Sé de los primeros en probar ArtiBook y ayúdanos a mejorar la experiencia.
          </p>

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 text-sm" data-testid="error-message">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label className="block text-gray-700 font-semibold mb-2 text-sm md:text-base" htmlFor="full_name">
                Nombre Completo <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="full_name"
                name="full_name"
                value={formData.full_name}
                onChange={handleChange}
                className="w-full px-4 py-2 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm md:text-base"
                placeholder="Tu nombre completo"
                required
                data-testid="input-full-name"
              />
            </div>

            <div className="mb-4">
              <label className="block text-gray-700 font-semibold mb-2 text-sm md:text-base" htmlFor="email">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full px-4 py-2 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm md:text-base"
                placeholder="tu@email.com"
                required
                data-testid="input-email"
              />
            </div>

            <div className="mb-4">
              <label className="block text-gray-700 font-semibold mb-2 text-sm md:text-base" htmlFor="role">
                Soy <span className="text-red-500">*</span>
              </label>
              <select
                id="role"
                name="role"
                value={formData.role}
                onChange={handleChange}
                className="w-full px-4 py-2 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm md:text-base"
                required
                data-testid="select-role"
              >
                <option value="">Selecciona una opción</option>
                <option value="dancer">Bailarín/a</option>
                <option value="musician">Músico/a</option>
                <option value="both">Ambos</option>
              </select>
            </div>

            <div className="mb-4">
              <label className="block text-gray-700 font-semibold mb-2 text-sm md:text-base" htmlFor="content_type">
                Tipo de contenido que quiero guardar (Opcional)
              </label>
              <input
                type="text"
                id="content_type"
                name="content_type"
                value={formData.content_type}
                onChange={handleChange}
                className="w-full px-4 py-2 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm md:text-base"
                placeholder="Ej: Coreografías de ballet, composiciones de jazz..."
                data-testid="input-content-type"
              />
            </div>

            <div className="mb-6">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  name="wants_updates"
                  checked={formData.wants_updates}
                  onChange={handleChange}
                  className="w-5 h-5 text-purple-600 rounded focus:ring-2 focus:ring-purple-500"
                  data-testid="checkbox-updates"
                />
                <span className="ml-2 text-gray-700 text-sm md:text-base">
                  Quiero recibir actualizaciones sobre ArtiBook
                </span>
              </label>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-purple-600 text-white py-3 rounded-lg font-bold text-base md:text-lg hover:bg-purple-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed"
              data-testid="submit-button"
            >
              {loading ? "Registrando..." : "Unirse a la Beta"}
            </button>
          </form>
        </div>

        {/* Footer */}
        <div className="text-center mt-12 md:mt-16 text-purple-200">
          <p className="text-xs md:text-sm">
            © 2025 ArtiBook - Donde la danza y la música se encuentran
          </p>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;